import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { fmtTL, fmtPay } from "@/lib/api";
import { GitBranch, Plus, Trash2, MapPin, Users, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Branches() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [staffBranch, setStaffBranch] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", type: "sube" });

  const { data: summary } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard/summary")).data });
  const { data: personnel = [] } = useQuery({ queryKey: ["personnel"], queryFn: async () => (await api.get("/personnel")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["branches"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const createM = useMutation({
    mutationFn: (b) => api.post("/branches", b),
    onSuccess: () => { invalidate(); setOpen(false); toast.success("Şube eklendi"); setForm({ name: "", location: "", type: "sube" }); },
  });
  const delM = useMutation({ mutationFn: (id) => api.delete(`/branches/${id}`), onSuccess: () => { invalidate(); toast.success("Şube silindi"); } });

  const branches = summary?.branch_breakdown || [];

  const submit = (e) => { e.preventDefault(); createM.mutate(form); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Şubeler</h1>
          <p className="text-slate-400 text-sm mt-1">Şube şube gider ve personel takibi</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-branch-button" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
              <Plus className="w-4 h-4" /> Şube Ekle
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0D1220] border-slate-800 text-white">
            <DialogHeader><DialogTitle className="font-heading">Yeni Şube</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Şube Adı</label>
                <input data-testid="branch-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Örn. Bakırköy Şubesi" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Konum</label>
                <input data-testid="branch-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Örn. Bakırköy, İstanbul" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Tür</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="branch-type-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0D1220] border-slate-700 text-white">
                    <SelectItem value="merkez">Merkez</SelectItem>
                    <SelectItem value="sube">Şube</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <button data-testid="branch-submit-button" type="submit" disabled={createM.isPending} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-lg text-sm">Kaydet</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((b) => (
          <div key={b.id} data-testid={`branch-card-${b.id}`} className="glass rounded-2xl p-5">
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-purple-500/15 text-purple-400 flex items-center justify-center">
                <GitBranch className="w-5 h-5" />
              </div>
              <button data-testid={`delete-branch-${b.id}`} onClick={() => delM.mutate(b.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-base font-heading font-semibold text-white">{b.name}</span>
              <span data-testid={`branch-type-badge-${b.id}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${b.type === "merkez" ? "bg-amber-500/15 text-amber-400" : "bg-purple-500/15 text-purple-400"}`}>{b.type === "merkez" ? "Merkez" : "Şube"}</span>
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {b.location || "—"}</div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <button type="button" data-testid={`branch-staff-${b.id}`} onClick={() => setStaffBranch(b)} className="rounded-xl bg-slate-800/50 hover:bg-blue-600/10 border border-transparent hover:border-blue-600/30 p-3 text-left transition-colors cursor-pointer">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Personel</div>
                <div className="text-lg font-bold text-white tabular mt-0.5">{b.staff}</div>
              </button>
              <div className="rounded-xl bg-slate-800/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Receipt className="w-3 h-3" /> Fatura</div>
                <div className="text-lg font-bold text-white tabular mt-0.5">{fmtTL(b.bills)}</div>
              </div>
            </div>
            <div className="mt-3 flex justify-between items-center border-t border-slate-800 pt-3">
              <span className="text-xs text-slate-400">Toplam Gider</span>
              <span className="font-heading font-bold text-white tabular">{fmtTL(b.total)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Branch personnel dialog */}
      <Dialog open={!!staffBranch} onOpenChange={(v) => !v && setStaffBranch(null)}>
        <DialogContent className="bg-[#0D1220] border-slate-800 text-white sm:max-w-md" data-testid="branch-staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" /> {staffBranch?.name} — Personel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {personnel.filter((p) => p.branch_id === staffBranch?.id).map((p) => (
              <div key={p.id} data-testid={`branch-staff-row-${p.id}`} className="flex items-center justify-between rounded-xl bg-slate-800/50 border border-slate-700/50 px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-white">{p.name}</div>
                  <div className="text-xs text-slate-400">{p.role || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Ödenecek Tutar</div>
                  <div className="text-sm font-heading font-bold text-white tabular">{fmtPay(p)}</div>
                </div>
              </div>
            ))}
            {personnel.filter((p) => p.branch_id === staffBranch?.id).length === 0 && (
              <div className="text-slate-500 text-sm py-8 text-center">Bu şubede personel bulunmuyor.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
